import type { DataScope } from '@crm/permissions';

/**
 * Who sees and who may own a customer (spec 004, rules 7 and 14–15). The API applies these
 * on every route; the web uses the same functions to offer only the owners and units the
 * API will accept, and the mocked API uses them to answer like the backend.
 *
 * Customers have no team: in the `team` scope a customer is the team's when its owner is a
 * teammate. Customers without an owner are the queue to claim, kept per unit: the `own`,
 * `team` and `unit` scopes see the unowned ones of their units, `organization` sees all.
 * Deals follow the same rules and also count their own team (`isRecordVisible`).
 */

export interface ScopeCaller {
  membershipId: string;
  dataScope: DataScope;
  unitIds: readonly string[];
  teamIds: readonly string[];
}

export interface ScopeDirectory {
  members: readonly { id: string; active: boolean; unitIds: readonly string[] }[];
  teams: readonly { id: string; active: boolean; memberIds: readonly string[] }[];
}

/** Members who share an active team with the caller (the caller included, when on a team). */
export function teammatesOf(caller: ScopeCaller, directory: ScopeDirectory): Set<string> {
  const ids = new Set<string>();
  for (const team of directory.teams) {
    if (team.active && caller.teamIds.includes(team.id)) {
      for (const member of team.memberIds) ids.add(member);
    }
  }
  return ids;
}

/** A record with an owner, a unit and, for deals, a team: who sees it (spec 004 rules 14–15). */
export function isRecordVisible(
  caller: ScopeCaller,
  directory: ScopeDirectory,
  record: { ownerMembershipId: string | null; unitId: string; teamId?: string | null },
): boolean {
  if (caller.dataScope === 'organization') return true;
  const owner = record.ownerMembershipId;
  if (owner === caller.membershipId) return true;
  // The unowned queue is per unit: whoever works in the unit may claim it.
  if (owner === null) return caller.unitIds.includes(record.unitId);
  if (caller.dataScope === 'own') return false;
  if (teammatesOf(caller, directory).has(owner)) return true;
  if (record.teamId && caller.teamIds.includes(record.teamId)) return true;
  return caller.dataScope === 'unit' && caller.unitIds.includes(record.unitId);
}

export function isCustomerVisible(
  caller: ScopeCaller,
  directory: ScopeDirectory,
  customer: { ownerMembershipId: string | null; unitId: string },
): boolean {
  return isRecordVisible(caller, directory, customer);
}

/** Whom the caller may make the owner: own → self; team → teammates; unit → their units. */
export function canAssignOwner(
  caller: ScopeCaller,
  directory: ScopeDirectory,
  membershipId: string,
): boolean {
  if (membershipId === caller.membershipId) return true;
  switch (caller.dataScope) {
    case 'own':
      return false;
    case 'team':
      return teammatesOf(caller, directory).has(membershipId);
    case 'unit': {
      const member = directory.members.find((candidate) => candidate.id === membershipId);
      return (
        teammatesOf(caller, directory).has(membershipId) ||
        (member?.unitIds.some((unitId) => caller.unitIds.includes(unitId)) ?? false)
      );
    }
    case 'organization':
      return true;
  }
}

/** The units a customer may be put in by the caller (all of them in the organization scope). */
export function canPlaceInUnit(caller: ScopeCaller, unitId: string): boolean {
  return caller.dataScope === 'organization' || caller.unitIds.includes(unitId);
}
